import Component from '../colorz/Component';
import getData from '@utils/html/getData';
import Device from '@utils/html/Device';
import {breakpointsApp} from '../colorz/configClrzApp';

export default class Tabs extends Component {
	onInit(el) {
		this.el = el;
		this.titles = this.el.querySelectorAll('[data-tab-title]');
		this.contents = this.el.querySelectorAll('[data-tab-content]');
		this.tablist = this.el.querySelector('[data-tablist]');
	}

	onReady() {
		// Initialize ARIA state based on current classes
		this.titles.forEach((title) => {
			const isActive = title.classList.contains('is-active');
			// Desktop tab buttons already rendered as <button role="tab">.
			// Mobile accordion triggers are also <button> but without role tab.
			if (title.closest('.xl\\:u-hidden')) {
				// Mobile trigger: manage aria-expanded instead
				title.setAttribute('aria-expanded', isActive ? 'true' : 'false');
			} else {
				// Desktop tab
				title.setAttribute('role', 'tab');
				title.setAttribute('aria-selected', isActive ? 'true' : 'false');
				// Only active tab should be tabbable
				title.setAttribute('tabindex', isActive ? '0' : '-1');
			}
		});

		this.contents.forEach((panel) => {
			const isActive = panel.classList.contains('is-active');
			if (!panel.hasAttribute('role')) {
				panel.setAttribute('role', 'tabpanel');
			}
			panel.setAttribute('tabindex', '0');
			panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
		});

		// Initial detection
		this.isMobile = Device.width < breakpointsApp.desktop;
		this.updatePanelAccessibility();

		// Handle resize events
		window.addEventListener('resize', () => {
			const currentWidth = window.innerWidth; // use real width for reliability
			const newIsMobile = currentWidth < breakpointsApp.desktop;

			if (newIsMobile !== this.isMobile) {
				this.isMobile = newIsMobile;
				this.updatePanelAccessibility();
			}
		});

		this.titles.forEach((title) => {
			title.addEventListener('click', () => {
				this.idTitle = getData(title, 'id');
				this.isMobile = window.innerWidth < breakpointsApp.desktop;

				if (title.classList.contains('is-active')) {
					if (this.isMobile) {
						// Mobile accordion: collapse
						title.classList.remove('is-active');
						title.setAttribute('aria-expanded', 'false');
						const panel = this.el.querySelector(
							'[data-content=' + this.idTitle + ']',
						);
						panel.classList.remove('is-active');
						panel.setAttribute('aria-hidden', 'true');
						panel.setAttribute('hidden', '');
					}
				} else {
					this.switchTab(this.idTitle, title);
				}
			});

			// Keyboard activation on Enter/Space for role=button (mobile) and role=tab (desktop)
			title.addEventListener('keydown', (event) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					title.click();
				}
			});
		});

		// Keyboard navigation for tabs (Up/Down/Home/End)
		if (this.tablist) {
			this.tablist.addEventListener('keydown', (event) => {
				const vertical =
					this.tablist.getAttribute('aria-orientation') === 'vertical';
				const keys = vertical
					? ['ArrowUp', 'ArrowDown', 'Home', 'End']
					: ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

				if (!keys.includes(event.key)) return;
				event.preventDefault();

				const tabs = Array.from(this.titles).filter(
					(t) => !t.closest('.xl\\:u-hidden'),
				); // desktop tabs
				const currentIndex = tabs.findIndex(
					(t) => t === document.activeElement,
				);
				let nextIndex = currentIndex;

				switch (event.key) {
					case 'ArrowUp':
					case 'ArrowLeft':
						nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
						break;
					case 'ArrowDown':
					case 'ArrowRight':
						nextIndex = (currentIndex + 1) % tabs.length;
						break;
					case 'Home':
						nextIndex = 0;
						break;
					case 'End':
						nextIndex = tabs.length - 1;
						break;
				}

				const nextTab = tabs[nextIndex];
				if (nextTab) {
					nextTab.focus();
					this.switchTab(getData(nextTab, 'id'), nextTab); // activation automatique
				}
			});
		}
	}

	updatePanelAccessibility() {
		this.contents.forEach((panel) => {
			if (this.isMobile) {
				panel.removeAttribute('tabindex');
				panel.removeAttribute('role');
			} else {
				panel.setAttribute('role', 'tabpanel');
				panel.setAttribute('tabindex', '0');
			}
		});
	}

	switchTab(id, title) {
		this.contents.forEach((content) => {
			this.idContent = getData(content, 'content');
			this.colorText = getData(content, 'color-text');
			this.colorBg = getData(content, 'color-bg');

			if (id == this.idContent) {
				if (this.el.querySelector('[data-tab-title].is-active')) {
					this.el
						.querySelector('[data-tab-title].is-active')
						.classList.remove('is-active');
				}

				if (this.el.querySelector('[data-tab-content].is-active')) {
					this.el
						.querySelector('[data-tab-content].is-active')
						.classList.remove('is-active');
				}

				content.classList.add('is-active');
				content.removeAttribute('hidden');
				content.setAttribute('aria-hidden', 'false');
				title.classList.add('is-active');

				if (title.closest('.xl\\:u-hidden')) {
					// Mobile accordion expand
					title.setAttribute('aria-expanded', 'true');
				} else {
					// Desktop tab selection
					title.setAttribute('aria-selected', 'true');
					// Only active tab should be tabbable
					title.setAttribute('tabindex', '0');
				}

				// Update other tabs/panels ARIA state
				this.titles.forEach((t) => {
					if (t !== title) {
						if (t.closest('.xl\\:u-hidden')) {
							// Other mobile triggers remain collapsed
							t.setAttribute('aria-expanded', 'false');
						} else {
							t.setAttribute('aria-selected', 'false');
							// Make other tabs not tabbable
							t.setAttribute('tabindex', '-1');
						}
					}
				});
				this.contents.forEach((c) => {
					if (c !== content) {
						c.setAttribute('aria-hidden', 'true');
						// Collapse mobile panels as well
						c.setAttribute('hidden', '');
					}
				});

				this.el.style.setProperty('--color-text', this.colorText);
				this.el.style.setProperty('--color-bg', this.colorBg);

				if (this.colorBg == '#fff' || this.colorBg == '#ffffff') {
					this.el.classList.remove('with-padding');
				} else {
					this.el.classList.add('with-padding');
				}
			}
		});
	}
}
